import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  AI_SPREADSHEET_OVERRIDE_OUTCOME,
  AI_SPREADSHEET_REGION_OVERRIDE_REVIEW_REASON,
  type IAiSpreadsheetRegionOverrideCandidate,
  type IAiSpreadsheetRegionOverrideReview,
} from "@/payroll/ai-script/types";
import { formatColumnLetter } from "./utils";

interface SpreadsheetRegionReviewBannerProps {
  reviews: IAiSpreadsheetRegionOverrideReview[];
  canConfirm: boolean;
  isConfirming: boolean;
  onConfirm: (
    review: IAiSpreadsheetRegionOverrideReview,
    candidate: IAiSpreadsheetRegionOverrideCandidate,
  ) => void;
}

const candidateLabel = (candidate: IAiSpreadsheetRegionOverrideCandidate) =>
  `Row ${candidate.header_row}, columns ${formatColumnLetter(
    candidate.start_col - 1,
  )}–${formatColumnLetter(candidate.end_col - 1)}`;

const reviewHeadline = (review: IAiSpreadsheetRegionOverrideReview) => {
  if (review.outcome === AI_SPREADSHEET_OVERRIDE_OUTCOME.unmapped) {
    return "This saved table no longer matches anything on the sheet.";
  }
  if (
    review.review_reason ===
    AI_SPREADSHEET_REGION_OVERRIDE_REVIEW_REASON.ambiguous
  ) {
    return "These headers now appear in more than one place. Pick the right one.";
  }
  if (
    review.review_reason ===
    AI_SPREADSHEET_REGION_OVERRIDE_REVIEW_REASON.conflict
  ) {
    return "This saved table collides with one the script generated. Pick the one to keep.";
  }
  return "This saved table needs review before it can be applied.";
};

/**
 * Surfaces region overrides the API parked after a rerun. Until one is resolved
 * the user's saved table — and its filter/sort setup — is simply absent from the
 * sheet, so this is the only path back to it.
 */
export const SpreadsheetRegionReviewBanner = ({
  reviews,
  canConfirm,
  isConfirming,
  onConfirm,
}: SpreadsheetRegionReviewBannerProps) => {
  if (reviews.length === 0) return null;

  return (
    <Stack align="start" direction="column" gap={2} className="w-full">
      {reviews.map((review) => (
        <Alert key={review.id} color="information">
          <WarningIcon className="h-5 w-5" />
          <AlertTitle>Saved table needs review</AlertTitle>
          <AlertDescription>
            <Stack align="start" direction="column" gap={2} className="w-full">
              <p>{reviewHeadline(review)}</p>
              <p className="text-subtle">
                Headers: {review.header_values.filter(Boolean).join(", ")}
              </p>
              {review.deletes_generated_region && (
                <p className="text-subtle">
                  Confirming keeps this table removed.
                </p>
              )}
              {canConfirm && review.candidates.length > 0 && (
                <Stack
                  align="start"
                  direction="row"
                  gap={2}
                  className="flex-wrap"
                >
                  {review.candidates.map((candidate) => (
                    <Button
                      key={`${candidate.header_row}:${candidate.start_col}:${candidate.end_col}`}
                      variant="outline"
                      size="xs"
                      disabled={isConfirming}
                      onClick={() => onConfirm(review, candidate)}
                    >
                      {candidateLabel(candidate)}
                    </Button>
                  ))}
                </Stack>
              )}
              {!canConfirm && (
                <p className="text-subtle">
                  You do not have permission to resolve this. Ask an editor to
                  review or save the table.
                </p>
              )}
              {canConfirm && review.candidates.length === 0 && (
                <p className="text-subtle">
                  Select the table's header row and choose Save table to set it
                  up again.
                </p>
              )}
            </Stack>
          </AlertDescription>
        </Alert>
      ))}
    </Stack>
  );
};
